import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRightLeft, Building, CalendarCheck2, CalendarPlus, CheckCircle2, GitMerge, Mail, MessageSquareText, Pencil, PhoneCall, RotateCcw,
  Sparkles, Trash2, UserRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { formatDate, formatDateTime, formatPhone, rupees, timeAgo } from '../../lib/format';
import { describeFollowup, useLeadSetup, type Activity, type LeadDetail } from '../../lib/leads';
import { Badge, Button, ButtonLink, ConfirmDialog, Drawer, ErrorState, Modal, SelectField, Skeleton, TextAreaField, cx } from '../ui';
import { FollowupPicker, PhoneActions, StatusPill, TagChips } from './bits';
import { useNavigate } from 'react-router';
import { LeadForm } from './LeadForm';
import { HeatBadge, LeadAiPanel, QuickNoteModal, useCanUseAi } from './LeadAi';
import { LeadSequences } from './LeadSequences';
import { LeadUnits } from './LeadUnits';
import { MergeDialog } from './MergeDialog';
import { DocLink } from '../../lib/links';
import { CallsTab, NotesTab, QuotesTab, TasksTab, noteCount, useLeadCalls, useLeadDocs, useLeadTasks } from './LeadTabs';
import type { QuickNoteResult } from '../../lib/ai';

export function useLead(id: string | null) {
  return useQuery({ queryKey: ['lead', id], queryFn: () => api.get<LeadDetail>(`/leads/${id}`), enabled: !!id });
}

/** Everything about one lead: quick actions on top, follow-up, notes and the full history. */
export function LeadDrawer({ id, onClose, onOpenLead }: { id: string; onClose: () => void; onOpenLead: (id: string) => void }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useLead(id);
  const setup = useLeadSetup().data;
  const [editing, setEditing] = useState(false);
  const [followupOpen, setFollowupOpen] = useState<'set' | 'done' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [tab, setTab] = useState<LeadTab>('timeline');
  const [logCall, setLogCall] = useState(false);
  const [merging, setMerging] = useState(false);
  const lead = q.data;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['lead', id] });
    void qc.invalidateQueries({ queryKey: ['leads'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
    void qc.invalidateQueries({ queryKey: ['followup-counts'] });
  };
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch(`/leads/${id}`, body),
    onSuccess: () => { toast.success('Saved.'); refresh(); },
    onError: (e) => toast.error(e.message),
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/leads/${id}`),
    onSuccess: () => { toast.success(`L-${lead?.number} moved to Trash. You can restore it within 30 days.`); refresh(); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  const restore = useMutation({
    mutationFn: () => api.post(`/leads/${id}/restore`),
    onSuccess: () => { toast.success('Lead restored.'); refresh(); },
    onError: (e) => toast.error(e.message),
  });

  const title = lead ? (
    <span className="flex flex-wrap items-center gap-2">
      <span className="truncate">{lead.name}</span>
      <span className="text-sm font-normal text-slate-500">L-{lead.number}</span>
      <HeatBadge label={lead.aiLabel} reason={lead.aiReason} />
      {lead.isQualified && <Badge tone="green">Qualified</Badge>}
      {lead.deletedAt && <Badge tone="red">In Trash</Badge>}
    </span>
  ) : 'Loading…';

  return (
    <Drawer
      open
      wide
      onClose={onClose}
      title={title}
      description={lead && (
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {lead.phone && <span>{formatPhone(lead.phone)}</span>}
          {lead.company && <span>{lead.company}</span>}
          {lead.city && <span>{lead.city}</span>}
          <span>Added {timeAgo(lead.createdAt)}</span>
        </span>
      )}
      headerExtra={lead && !lead.deletedAt && can('leads.edit') && (
        <Button variant="ghost" size="sm" icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>Edit</Button>
      )}
    >
      {q.isPending ? (
        <div className="space-y-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : q.isError || !lead ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : lead.deletedAt ? (
        <div className="rounded-xl bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950 dark:text-red-200">
          This lead is in the Trash.
          {can('leads.delete') && <Button size="sm" className="ml-3" icon={<RotateCcw className="size-4" />} onClick={() => restore.mutate()} loading={restore.isPending}>Restore</Button>}
        </div>
      ) : (
        <div className="space-y-5">
          {/* Quick actions */}
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-44 flex-1">
              <SelectField label="Status" value={lead.statusId} disabled={!can('leads.edit') || patch.isPending}
                onChange={(e) => patch.mutate({ statusId: e.target.value })}>
                {setup?.statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </SelectField>
            </div>
            <div className="min-w-44 flex-1">
              {can('leads.assign') ? (
                <SelectField label="Assigned to" value={lead.ownerId ?? ''} disabled={patch.isPending}
                  onChange={(e) => patch.mutate({ ownerId: e.target.value || null })}>
                  <option value="">— Nobody —</option>
                  {setup?.users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </SelectField>
              ) : (
                <div><p className="mb-1.5 text-sm font-medium">Assigned to</p><p className="flex h-10 items-center gap-2 text-sm"><UserRound className="size-4 text-slate-400" />{lead.ownerName ?? 'Nobody'}</p></div>
              )}
            </div>
            <PhoneActions phone={lead.phone} />
            {lead.phone && can('whatsapp.chat') && <OpenChatButton leadId={lead.id} />}
            {can('documents.manage') && <ButtonLink to={`/sales/documents/new?type=quotation&forLead=${lead.id}`} variant="secondary" size="sm" className="h-9">Make quotation</ButtonLink>}
            {lead.email && (
              <a href={`mailto:${lead.email}`} className="inline-flex size-9 items-center justify-center rounded-lg ring-1 ring-slate-200 hover:bg-slate-50 dark:ring-slate-700" aria-label="Send email">
                <Mail className="size-4 text-slate-600" />
              </a>
            )}
          </div>

          <FollowupCard lead={lead} onSet={() => setFollowupOpen('set')} onDone={() => setFollowupOpen('done')} onCleared={refresh} />
          {lead.phone && (
            <div className="-mt-2 text-right">
              <button className="text-sm font-medium text-brand-700 hover:underline" onClick={() => setLogCall(true)}>+ Log a call made from another phone</button>
            </div>
          )}

          <LeadAiPanel lead={lead} onChanged={refresh} />

          <LeadUnits leadId={lead.id} />

          <NoteComposer lead={lead} onSaved={refresh} />

          <LeadTabBar lead={lead} tab={tab} onTab={setTab} />
          {tab === 'timeline' && <Timeline items={lead.activities} />}
          {tab === 'notes' && <NotesTab lead={lead} />}
          {tab === 'calls' && <CallsTab leadId={lead.id} />}
          {tab === 'quotes' && <QuotesTab lead={lead} />}
          {tab === 'tasks' && <TasksTab lead={lead} />}
          {tab === 'details' && <Details lead={lead} />}

          {(can('leads.delete') || can('leads.edit')) && (
            <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
              {can('leads.edit') && <Button variant="ghost" size="sm" icon={<GitMerge className="size-4" />} onClick={() => setMerging(true)}>Merge a duplicate into this lead</Button>}
              {can('leads.delete') && <Button variant="ghost" size="sm" className="text-red-600" icon={<Trash2 className="size-4" />} onClick={() => setConfirmDelete(true)}>Move to Trash</Button>}
            </div>
          )}
        </div>
      )}

      {editing && lead && <LeadForm lead={lead} onClose={() => setEditing(false)} onOpenLead={onOpenLead} onSaved={refresh} />}
      {merging && lead && <MergeDialog lead={lead} onClose={() => setMerging(false)} onMerged={refresh} />}
      {logCall && lead && <LogCallDialog leadId={lead.id} onClose={() => setLogCall(false)} onSaved={refresh} />}
      {followupOpen && lead && <FollowupDialog lead={lead} mode={followupOpen} onClose={() => setFollowupOpen(null)} onSaved={refresh} />}
      <ConfirmDialog open={confirmDelete} onClose={() => setConfirmDelete(false)} onConfirm={() => remove.mutate()} loading={remove.isPending} danger
        title={`Move L-${lead?.number} to Trash?`} message="You can restore it from Leads → Trash for 30 days." confirmLabel="Move to Trash" />
    </Drawer>
  );
}

function FollowupCard({ lead, onSet, onDone, onCleared }: { lead: LeadDetail; onSet: () => void; onDone: () => void; onCleared: () => void }) {
  const f = describeFollowup(lead.nextFollowupAt);
  const clear = useMutation({ mutationFn: () => api.del(`/leads/${lead.id}/followup`), onSuccess: onCleared });
  if (!lead.nextFollowupAt) {
    const closed = lead.stage === 'won' || lead.stage === 'lost';
    return (
      <div className={cx('flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4', closed ? 'border-slate-200 dark:border-slate-800' : 'border-dashed border-amber-300 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/30')}>
        <p className="text-sm">{closed ? 'No follow-up needed — this lead is closed.' : <><strong>No follow-up set.</strong> Leads without a next step are easy to forget.</>}</p>
        <Button size="sm" variant={closed ? 'secondary' : 'primary'} icon={<CalendarPlus className="size-4" />} onClick={onSet}>Set follow-up</Button>
      </div>
    );
  }
  return (
    <div className={cx('flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4', f.tone === 'overdue' ? 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40' : 'border-brand-100 bg-brand-50/60 dark:border-slate-700 dark:bg-slate-800/50')}>
      <div className="text-sm">
        <p className={cx('font-semibold', f.tone === 'overdue' && 'text-red-700 dark:text-red-300')}>Follow up: {f.text}</p>
        {lead.followupNote && <p className="mt-0.5 text-slate-700 dark:text-slate-300">{lead.followupNote}</p>}
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="ghost" onClick={() => clear.mutate()} loading={clear.isPending}>Remove</Button>
        <Button size="sm" variant="secondary" onClick={onSet}>Reschedule</Button>
        <Button size="sm" icon={<CheckCircle2 className="size-4" />} onClick={onDone}>Done</Button>
      </div>
    </div>
  );
}

function FollowupDialog({ lead, mode, onClose, onSaved }: { lead: LeadDetail; mode: 'set' | 'done'; onClose: () => void; onSaved: () => void }) {
  const setup = useLeadSetup().data;
  const [at, setAt] = useState(mode === 'set' && lead.nextFollowupAt && new Date(lead.nextFollowupAt) > new Date() ? lead.nextFollowupAt : '');
  const [note, setNote] = useState(mode === 'set' ? lead.followupNote ?? '' : '');
  const [outcome, setOutcome] = useState('');
  const [statusId, setStatusId] = useState(lead.statusId);
  const [error, setError] = useState<string>();
  const closing = setup?.statuses.find((s) => s.id === statusId)?.stage;
  const isClosed = closing === 'won' || closing === 'lost';

  const save = useMutation({
    mutationFn: () =>
      mode === 'set'
        ? api.post(`/leads/${lead.id}/followup`, { at, note })
        : api.post(`/leads/${lead.id}/followup/done`, { note: outcome, statusId, nextAt: at || null, nextNote: note }),
    onSuccess: () => { toast.success(mode === 'set' ? 'Follow-up saved.' : 'Marked as done.'); onSaved(); onClose(); },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Modal open onClose={onClose} title={mode === 'set' ? 'Set a follow-up' : 'How did it go?'} size="md"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button loading={save.isPending} onClick={() => {
          if (mode === 'set' && !at) { setError('Pick when to follow up.'); return; }
          save.mutate();
        }}>Save</Button></>}>
      <div className="space-y-5">
        {mode === 'done' && (
          <>
            <TextAreaField label="What happened?" placeholder="e.g. Spoke to him, wants a quote for 200 guests" value={outcome} onChange={(e) => setOutcome(e.target.value)} autoFocus />
            <SelectField label="Status now" value={statusId} onChange={(e) => setStatusId(e.target.value)}>
              {setup?.statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </SelectField>
            <p className="text-sm font-medium">{isClosed ? 'Next follow-up (optional)' : 'When is the next follow-up?'}</p>
          </>
        )}
        <FollowupPicker value={at} onChange={(v) => { setAt(v); setError(undefined); }} note={note} onNoteChange={setNote} error={error} />
      </div>
    </Modal>
  );
}

function NoteComposer({ lead, onSaved }: { lead: LeadDetail; onSaved: () => void }) {
  const leadId = lead.id;
  const [body, setBody] = useState('');
  const ai = useCanUseAi();
  const [tidied, setTidied] = useState<QuickNoteResult | null>(null);
  const save = useMutation({
    mutationFn: () => api.post(`/leads/${leadId}/notes`, { body }),
    onSuccess: () => { setBody(''); onSaved(); },
    onError: (e) => toast.error(e.message),
  });
  const tidy = useMutation({
    mutationFn: () => api.post<QuickNoteResult>(`/ai/leads/${leadId}/quick-note`, { text: body }),
    onSuccess: setTidied,
    onError: (e) => toast.error(e.message),
  });
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (body.trim()) save.mutate(); }} className="rounded-xl border border-slate-200 p-3 focus-within:border-brand-500 dark:border-slate-700">
      <label htmlFor={`note-${leadId}`} className="sr-only">Add a note</label>
      <textarea id={`note-${leadId}`} rows={2} value={body} onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && body.trim()) save.mutate(); }}
        placeholder={ai.show ? 'Add a note — e.g. “spoke to him, wants quote for 200 pax, call tomorrow 11am”' : 'Add a note — what did they say?'}
        className="block w-full resize-none border-0 bg-transparent p-0 text-sm focus:ring-0 focus:outline-none" />
      <div className="mt-2 flex justify-end gap-2">
        {ai.show && (
          <Button size="sm" variant="ghost" icon={<Sparkles className="size-4 text-brand-600" />} disabled={body.trim().length < 3 || !ai.ready} loading={tidy.isPending}
            onClick={() => tidy.mutate()} title="AI tidies the note and picks out the follow-up time and status">
            Tidy with AI
          </Button>
        )}
        <Button size="sm" type="submit" disabled={!body.trim()} loading={save.isPending}>Save note</Button>
      </div>
      {tidied && <QuickNoteModal lead={lead} result={tidied} onClose={() => setTidied(null)} onSaved={() => { setBody(''); onSaved(); }} />}
    </form>
  );
}

const ICONS: Record<string, typeof MessageSquareText> = {
  note: MessageSquareText, status: ArrowRightLeft, assigned: UserRound, unassigned: UserRound, followup_set: CalendarPlus,
  followup_done: CalendarCheck2, followup_cleared: CalendarPlus, call: PhoneCall, created: Sparkles, field: Pencil,
  deleted: Trash2, restored: RotateCcw, import: Sparkles, call_ai: Sparkles, unit: Building, merged: GitMerge,
};

function describe(a: Activity): string {
  switch (a.type) {
    case 'created': return a.body ?? 'Lead added';
    case 'note': return 'Note';
    case 'call': return (a.body ?? 'Call').split('\n')[0]!;
    case 'status': return `Status changed: ${a.body}`;
    case 'assigned': return `Assigned to ${a.toName ?? 'someone'}${(a.meta as { auto?: boolean })?.auto ? ' (automatic)' : ''}`;
    case 'unassigned': return 'Unassigned';
    case 'followup_set': return `Follow-up set for ${formatDateTime(String((a.meta as { at?: string })?.at ?? ''))}`;
    case 'followup_done': return 'Follow-up done';
    case 'followup_cleared': return 'Follow-up removed';
    case 'field': return a.body ?? 'Details updated';
    case 'deleted': return 'Moved to Trash';
    case 'restored': return 'Restored from Trash';
    case 'import': return a.body ?? 'Updated from import';
    case 'call_ai': return 'AI call summary';
    default: return a.body ?? a.type;
  }
}

type LeadTab = 'timeline' | 'notes' | 'calls' | 'quotes' | 'tasks' | 'details';

/** History · Notes · Calls · Quotes · Tasks · Details, with how many of each. */
function LeadTabBar({ lead, tab, onTab }: { lead: LeadDetail; tab: LeadTab; onTab: (t: LeadTab) => void }) {
  const calls = useLeadCalls(lead.id).data?.total;
  const docs = useLeadDocs(lead.id).data?.length;
  const tasks = useLeadTasks(lead.id).data?.filter((t) => t.status !== 'done').length;
  const tabs: [LeadTab, string, number | undefined][] = [
    ['timeline', 'History', undefined], ['notes', 'Notes', noteCount(lead.activities)], ['calls', 'Calls', calls],
    ['quotes', 'Quotes', docs], ['tasks', 'Tasks', tasks], ['details', 'Details', undefined],
  ];
  return (
    <div className="-mx-1 overflow-x-auto border-b border-slate-200 dark:border-slate-800">
      <nav className="-mb-px flex gap-5 px-1" aria-label="Lead sections">
        {tabs.map(([t, label, n]) => (
          <button key={t} onClick={() => onTab(t)} aria-current={tab === t}
            className={cx('shrink-0 border-b-2 pb-2 text-sm font-medium whitespace-nowrap', tab === t ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-800')}>
            {label}{n ? <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">{n}</span> : null}
          </button>
        ))}
      </nav>
    </div>
  );
}

function Timeline({ items }: { items: Activity[] }) {
  if (!items.length) return <p className="text-sm text-slate-500">Nothing yet.</p>;
  return (
    <ol className="space-y-4">
      {items.map((a) => {
        const Icon = ICONS[a.type] ?? Sparkles;
        const callNote = a.type === 'call' && a.body?.includes('\n') ? a.body.split('\n').slice(1).join('\n') : null;
        const aiSummary = a.type === 'call_ai' ? a.body?.replace(/^AI call summary: /, '') : null;
        const showBody = callNote ?? aiSummary ?? ((a.type === 'note' || a.type === 'followup_done' || a.type === 'followup_set') && a.body);
        return (
          <li key={a.id} className="flex gap-3">
            <div className={cx('mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full', a.type === 'note' || a.type === 'followup_done' ? 'bg-brand-50 text-brand-600' : 'bg-slate-100 text-slate-500 dark:bg-slate-800')}>
              <Icon className="size-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm">
                {/* Quotations, invoices and payments link to the document. */}
                {typeof a.meta?.documentId === 'string'
                  ? <DocLink id={a.meta.documentId} className="font-medium">{describe(a)}</DocLink>
                  : <span className="font-medium">{describe(a)}</span>}
              </p>
              {showBody && <p className="mt-1 rounded-lg bg-slate-50 px-3 py-2 text-sm whitespace-pre-wrap dark:bg-slate-800">{aiSummary ?? a.body}</p>}
              <p className="mt-0.5 text-xs text-slate-500">{a.userName ?? 'System'} · {formatDateTime(a.createdAt)}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Details({ lead }: { lead: LeadDetail }) {
  const setup = useLeadSetup().data;
  const rows: [string, React.ReactNode][] = [
    ['Status', <StatusPill key="s" name={lead.statusName} color={lead.statusColor} />],
    ['Phone', lead.phone ? formatPhone(lead.phone) : null],
    ['Other phone', lead.altPhone ? formatPhone(lead.altPhone) : null],
    ['Email', lead.email],
    ['Company', lead.company],
    ['City', lead.city],
    ['State', lead.state],
    ['Address', lead.address],
    ['Source', lead.sourceName],
    ['Campaign', lead.campaignName],
    ['Deal value', lead.value !== null ? rupees(lead.value) : null],
    ['Tags', lead.tags.length ? <TagChips key="t" tags={lead.tags} max={20} /> : null],
    ...(setup?.customFields ?? []).map((f) => [f.label, formatCustom(lead.custom[f.key])] as [string, React.ReactNode]),
    ['Added by', lead.createdByName ? `${lead.createdByName} on ${formatDate(lead.createdAt)}` : formatDate(lead.createdAt)],
  ];
  return (
    <div className="space-y-4">
      {lead.notes && (
        <div>
          <p className="text-sm font-medium text-slate-500">Requirement</p>
          <p className="mt-1 text-sm whitespace-pre-wrap">{lead.notes}</p>
        </div>
      )}
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
        {rows.filter(([, v]) => v !== null && v !== undefined && v !== '').map(([k, v]) => (
          <div key={k}><dt className="text-sm text-slate-500">{k}</dt><dd className="text-sm font-medium break-words">{v}</dd></div>
        ))}
      </dl>
      <LeadSequences leadId={lead.id} />
    </div>
  );
}

function formatCustom(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

function LogCallDialog({ leadId, onClose, onSaved }: { leadId: string; onClose: () => void; onSaved: () => void }) {
  const [direction, setDirection] = useState<'outgoing' | 'incoming'>('outgoing');
  const [connected, setConnected] = useState(true);
  const [minutes, setMinutes] = useState('2');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => api.post('/calls', { leadId, direction, connected, durationMin: Number(minutes) || 0, note: note || undefined }),
    onSuccess: () => { toast.success('Call saved.'); onSaved(); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Modal open onClose={onClose} title="Log a call" size="sm"
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button loading={save.isPending} onClick={() => save.mutate()}>Save call</Button></>}>
      <div className="space-y-4">
        <SelectField label="Call type" value={direction} onChange={(e) => setDirection(e.target.value as 'outgoing' | 'incoming')}>
          <option value="outgoing">I called them</option><option value="incoming">They called me</option>
        </SelectField>
        <SelectField label="Did you speak?" value={connected ? 'yes' : 'no'} onChange={(e) => setConnected(e.target.value === 'yes')}>
          <option value="yes">Yes, we spoke</option><option value="no">No answer</option>
        </SelectField>
        {connected && (
          <SelectField label="How long?" value={minutes} onChange={(e) => setMinutes(e.target.value)}>
            {['1', '2', '5', '10', '15', '30'].map((m) => <option key={m} value={m}>{m} minute{m === '1' ? '' : 's'}</option>)}
          </SelectField>
        )}
        <TextAreaField label="Notes (optional)" value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was discussed?" />
      </div>
    </Modal>
  );
}

/** Opens (or starts) the WhatsApp Business conversation for this lead in the shared inbox. */
function OpenChatButton({ leadId }: { leadId: string }) {
  const navigate = useNavigate();
  const open = useMutation({
    mutationFn: () => api.post<{ threadId: string }>(`/leads/${leadId}/whatsapp`),
    onSuccess: (r) => navigate(`/whatsapp?thread=${r.threadId}`),
    onError: (e) => toast.error(e.message),
  });
  return <Button variant="secondary" size="sm" className="h-9" loading={open.isPending} onClick={() => open.mutate()}>Chat in CRM</Button>;
}
